import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';

// project imports
import IconButton from '@/components/@extended/IconButton';
import Avatar from '@/components/@extended/Avatar';
import MainCard from '@/components/MainCard';
import SimpleBar from '@/components/third-party/SimpleBar';
import BellOutlined from '@ant-design/icons/BellOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import {
	useChatNotifications,
	ChatNotificationEntry
} from '@/providers/chat-notifications/provider';

// ==============================|| HEADER - NOTIFICĂRI CHAT ||============================== //

const relTime = (iso: string): string => {
	const t = new Date(iso).getTime();
	if (Number.isNaN(t)) return '';
	const min = Math.floor((Date.now() - t) / 60000);
	if (min < 1) return 'acum';
	if (min < 60) return `${min}m`;
	const h = Math.floor(min / 60);
	if (h < 24) return `${h}h`;
	const d = Math.floor(h / 24);
	if (d < 7) return `${d}z`;
	return `${Math.floor(d / 7)}săpt`;
};

export default function NotificationMenu() {
	const navigate = useNavigate();
	const { items, unreadTotal, markAllRead, markRoomRead } = useChatNotifications();
	const [open, setOpen] = useState(false);
	const handleToggle = () => setOpen((v) => !v);

	const openRoom = (entry: ChatNotificationEntry) => {
		markRoomRead(entry.roomId); // scade badge-ul instant (pagina de chat marchează citit pe server)
		setOpen(false);
		navigate(`/common/chat?room=${entry.roomId}`);
	};

	return (
		<>
			<Box sx={{ flexShrink: 0 }}>
				<Tooltip title="Notificări">
					<IconButton
						color="secondary"
						variant="light"
						sx={(theme) => ({
							color: 'text.primary',
							bgcolor: open ? 'grey.100' : 'transparent',
							...theme.applyStyles('dark', {
								bgcolor: open ? 'background.default' : 'transparent'
							})
						})}
						aria-label="deschide notificările"
						onClick={handleToggle}
					>
						<Badge badgeContent={unreadTotal > 99 ? '99+' : unreadTotal} color="primary">
							<BellOutlined />
						</Badge>
					</IconButton>
				</Tooltip>
			</Box>

			<Drawer
				sx={{ zIndex: 2001 }}
				anchor="right"
				onClose={handleToggle}
				open={open}
				slotProps={{ paper: { sx: { width: { xs: 340, sm: 440 } } } }}
			>
				{open && (
					<MainCard
						title={<Typography variant="h5">Notificări</Typography>}
						sx={{
							border: 'none',
							borderRadius: 0,
							height: '100vh',
							'& .MuiCardHeader-root': { p: 2.25 }
						}}
						content={false}
						secondary={
							items.length ? (
								<Link
									component="button"
									underline="hover"
									sx={{ typography: 'caption', textTransform: 'none' }}
									onClick={() => markAllRead()}
								>
									Marchează toate citite
								</Link>
							) : undefined
						}
					>
						<SimpleBar
							sx={{ overflowX: 'hidden', height: 'calc(100vh - 90px)', minHeight: 420 }}
						>
							{!items.length ? (
								<Stack
									sx={{ alignItems: 'center', justifyContent: 'center', py: 8, px: 3 }}
								>
									<Typography color="text.secondary" variant="body2">
										Nicio notificare nouă.
									</Typography>
								</Stack>
							) : (
								<List component="nav" disablePadding>
									{items.map((entry) => (
										<ListItemButton
											key={entry.roomId}
											divider
											onClick={() => openRoom(entry)}
											sx={{ py: 1.25, gap: 1.5, alignItems: 'flex-start' }}
										>
											<Avatar
												src={entry.imageUrl || undefined}
												color="primary"
												type="combined"
												size="sm"
											>
												<MessageOutlined />
											</Avatar>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
													<Typography
														variant="subtitle1"
														noWrap
														sx={{ flex: 1, minWidth: 0 }}
													>
														{entry.title}
													</Typography>
													<Chip
														size="small"
														color="primary"
														label={entry.unreadCount}
														sx={{ height: 20 }}
													/>
												</Stack>
												<Stack
													direction="row"
													sx={{ alignItems: 'center', gap: 1, mt: 0.25 }}
												>
													<Typography
														variant="caption"
														color="text.secondary"
														noWrap
														sx={{ flex: 1, minWidth: 0 }}
													>
														{entry.preview || 'Mesaje noi'}
													</Typography>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ whiteSpace: 'nowrap' }}
													>
														{relTime(entry.lastActivity)}
													</Typography>
												</Stack>
											</Box>
										</ListItemButton>
									))}
								</List>
							)}
						</SimpleBar>

						{items.length > 0 && (
							<Stack sx={{ py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
								<Link
									component="button"
									underline="hover"
									sx={{ alignSelf: 'center', typography: 'body2' }}
									onClick={() => {
										setOpen(false);
										navigate('/common/chat');
									}}
								>
									Deschide Mesaje
								</Link>
							</Stack>
						)}
					</MainCard>
				)}
			</Drawer>
		</>
	);
}
